import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  Warehouse, 
  ClipboardList, 
  HardHat,
  Wrench,
  UsersRound,
  UserCheck,
  Menu,
  X,
  LogOut,
  ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NEW_LOGO_URL = "https://customer-assets.emergentagent.com/job_procurement-central-1/artifacts/itphun63_FullLogo_NoBuffer%20%281%29.png";

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'manager', 'installer'] },
  { to: '/vendors', icon: Users, label: 'Vendors', roles: ['admin', 'manager'] },
  { to: '/products', icon: Package, label: 'Products', roles: ['admin', 'manager'] },
  { to: '/inventory', icon: Warehouse, label: 'Inventory', roles: ['admin', 'manager'] },
  { to: '/purchase-orders', icon: ClipboardList, label: 'Purchase Orders', roles: ['admin', 'manager'] },
  { to: '/installations', icon: Wrench, label: 'Installations', roles: ['admin', 'manager', 'installer'] },
  { to: '/customers', icon: UserCheck, label: 'Customers', roles: ['admin', 'manager'] },
  { to: '/installers', icon: HardHat, label: 'Installers', roles: ['admin', 'manager'] },
  { to: '/team', icon: UsersRound, label: 'Team', roles: ['admin'] },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const roleColors = {
    admin: 'bg-primary text-primary-foreground',
    manager: 'bg-blue-100 text-blue-800',
    installer: 'bg-green-100 text-green-800',
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="flex lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border px-4 py-3 items-center justify-between">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setSidebarOpen(true)}
          data-testid="btn-open-menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <img 
          src={NEW_LOGO_URL} 
          alt="Solar Paradigm" 
          className="h-8 w-auto"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1">
              <span className="text-sm font-medium truncate max-w-[100px]">{user?.name}</span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{user?.name}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-border flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b border-border flex items-center justify-between">
          <img 
            src={NEW_LOGO_URL} 
            alt="Solar Paradigm" 
            className="h-8 lg:h-10 w-auto"
            data-testid="company-logo"
          />
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto" data-testid="sidebar-nav">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 lg:px-6 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary border-r-2 border-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        
        {/* User Section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <span className={`inline-block px-2 py-0.5 text-xs rounded-full capitalize ${roleColors[user?.role] || 'bg-gray-100'}`}>
                {user?.role}
              </span>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full rounded-sm"
            onClick={handleLogout}
            data-testid="btn-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <div className="max-w-[1600px] mx-auto p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
